import React from "react";

function shade(hex, amt) {
  if (!hex || hex[0] !== "#") return hex || "#000";
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, Math.min(255, (n >> 16) + amt));
  const g = Math.max(0, Math.min(255, ((n >> 8) & 0xff) + amt));
  const b = Math.max(0, Math.min(255, (n & 0xff) + amt));
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Polished, modular vector-portrait avatar (NO AI). Distinct woman / man.
 * Interactive: lip / blush / eyeshadow update instantly. Precise & shareable.
 */
export default function Avatar({ profile = {}, options, lip, blush, eyeshadow, hairHex, size = 320 }) {
  const find = (list, id) => (options && list ? (options[list] || []).find((x) => x.id === id) : null);
  const skin = find("skin_tones", profile.skin_tone)?.hex || "#E7C1A3";
  const hair = hairHex || find("hair_colors", profile.hair_color)?.hex || "#4A3524";
  const eyeC = find("eye_colors", profile.eye_color)?.hex || "#5A3A22";
  const length = profile.hair_length || (profile.gender === "man" ? "short" : "long");
  const isMan = profile.gender === "man";
  const uid = React.useId().replace(/:/g, "");
  const [tilt, setTilt] = React.useState({ x: 0, y: 0 });
  const [blink, setBlink] = React.useState(false);
  React.useEffect(() => {
    const id = setInterval(() => { setBlink(true); setTimeout(() => setBlink(false), 150); }, 4200);
    return () => clearInterval(id);
  }, []);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTilt({ x: (e.clientX - r.left) / r.width - 0.5, y: (e.clientY - r.top) / r.height - 0.5 });
  };

  const sLight = shade(skin, 20), sMid = skin, sDark = shade(skin, -24), sDeep = shade(skin, -46);
  const hLight = shade(hair, 40), hDark = shade(hair, -40);
  const lipC = lip || (isMan ? shade(skin, -46) : "#C9727E");
  const lipHi = shade(lipC, 44), lipLo = shade(lipC, -34);
  const brow = shade(hair, -14);

  const g = (id) => `url(#${id}${uid})`;

  return (
    <div onMouseMove={onMove} onMouseLeave={() => setTilt({ x: 0, y: 0 })} className="animate-float"
      style={{ perspective: "800px", width: size, maxWidth: "100%" }} data-testid="avatar-stage">
    <svg viewBox="0 0 320 380" preserveAspectRatio="xMidYMid meet"
      style={{ display: "block", width: "100%", height: "auto", transform: `rotateY(${tilt.x * 16}deg) rotateX(${-tilt.y * 11}deg)`, transition: "transform 0.18s ease-out", transformStyle: "preserve-3d" }}
      data-testid="dynamic-avatar">
      <defs>
        <radialGradient id={`bg${uid}`} cx="50%" cy="32%" r="75%">
          <stop offset="0%" stopColor="#F7F3E8" />
          <stop offset="70%" stopColor="#E6EEE3" />
          <stop offset="100%" stopColor="#CFDECB" />
        </radialGradient>
        <linearGradient id={`hair${uid}`} x1="0.1" y1="0" x2="0.9" y2="1">
          <stop offset="0%" stopColor={hLight} />
          <stop offset="45%" stopColor={hair} />
          <stop offset="100%" stopColor={hDark} />
        </linearGradient>
        <linearGradient id={`skin${uid}`} x1="0.25" y1="0.05" x2="0.8" y2="1">
          <stop offset="0%" stopColor={sLight} />
          <stop offset="55%" stopColor={sMid} />
          <stop offset="100%" stopColor={sDark} />
        </linearGradient>
        <radialGradient id={`cheek${uid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={blush || "#E88F98"} stopOpacity="0.6" />
          <stop offset="100%" stopColor={blush || "#E88F98"} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`iris${uid}`} cx="50%" cy="45%" r="55%">
          <stop offset="0%" stopColor={shade(eyeC, 45)} />
          <stop offset="55%" stopColor={eyeC} />
          <stop offset="100%" stopColor={shade(eyeC, -45)} />
        </radialGradient>
        <linearGradient id={`lip${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lipHi} />
          <stop offset="45%" stopColor={lipC} />
          <stop offset="100%" stopColor={lipLo} />
        </linearGradient>
        <linearGradient id={`cloth${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#33402F" />
          <stop offset="100%" stopColor="#1B211B" />
        </linearGradient>
      </defs>

      <circle cx="160" cy="176" r="176" fill={g("bg")} />

      {/* BACK HAIR (woman) */}
      {!isMan && length !== "short" && (
        <path d={length === "long"
          ? "M66 152 C56 66 112 30 160 30 C208 30 264 66 254 152 C272 258 254 372 214 380 L106 380 C66 372 48 258 66 152 Z"
          : "M72 152 C64 70 112 38 160 38 C208 38 256 70 248 152 C260 232 246 300 220 308 L100 308 C74 300 60 232 72 152 Z"}
          fill={g("hair")} />
      )}

      {/* SHOULDERS + NECK */}
      <path d="M56 380 C62 316 106 292 160 292 C214 292 258 316 264 380 Z" fill={g("cloth")} />
      <path d="M133 246 L133 296 C133 310 145 318 160 318 C175 318 187 310 187 296 L187 246 Z" fill={g("skin")} />
      <path d="M133 260 C143 280 177 280 187 260 L187 246 L133 246 Z" fill={sDeep} opacity="0.45" />

      {/* EARS */}
      <ellipse cx="88" cy="178" rx="14" ry="21" fill={sMid} />
      <ellipse cx="232" cy="178" rx="14" ry="21" fill={sMid} />
      <path d="M84 172 q6 6 2 14" stroke={sDeep} strokeWidth="2" fill="none" opacity="0.5" />
      <path d="M236 172 q-6 6 -2 14" stroke={sDeep} strokeWidth="2" fill="none" opacity="0.5" />
      {!isMan && (<>
        <circle cx="88" cy="200" r="4.5" fill="#E6C287" /><circle cx="232" cy="200" r="4.5" fill="#E6C287" />
      </>)}

      {/* FACE */}
      {isMan ? (
        <path d="M92 156 C92 104 118 74 160 74 C202 74 228 104 228 156 C228 200 214 244 186 258 C176 263 168 265 160 265 C152 265 144 263 134 258 C106 244 92 200 92 156 Z" fill={g("skin")} />
      ) : (
        <path d="M98 154 C98 102 122 78 160 78 C198 78 222 102 222 154 C222 202 202 244 176 256 C170 259 165 261 160 261 C155 261 150 259 144 256 C118 244 98 202 98 154 Z" fill={g("skin")} />
      )}
      {/* forehead + temple highlight */}
      <ellipse cx="160" cy="120" rx="46" ry="34" fill={sLight} opacity="0.35" />
      {/* jaw / cheek shadow */}
      <path d="M104 200 C120 244 200 244 216 200 C210 246 188 262 160 264 C132 262 110 246 104 200 Z" fill={sDeep} opacity="0.22" />
      <path d="M118 150 C110 178 112 206 128 224" stroke={sDeep} strokeWidth="8" fill="none" opacity="0.12" strokeLinecap="round" />
      <path d="M202 150 C210 178 208 206 192 224" stroke={sDeep} strokeWidth="8" fill="none" opacity="0.12" strokeLinecap="round" />

      {/* cheeks blush */}
      <ellipse cx="120" cy="196" rx="24" ry="17" fill={g("cheek")} />
      <ellipse cx="200" cy="196" rx="24" ry="17" fill={g("cheek")} />

      {/* EYESHADOW (woman / makeup) */}
      {eyeshadow && !isMan && (<>
        <path d="M106 168 Q124 152 146 164 Q126 160 106 172 Z" fill={eyeshadow} opacity="0.7" />
        <path d="M174 164 Q196 152 214 168 Q194 160 174 172 Z" fill={eyeshadow} opacity="0.7" />
      </>)}

      {/* EYEBROWS */}
      <path d={isMan ? "M104 152 Q130 140 154 150 L152 158 Q130 149 106 160 Z" : "M108 154 Q130 143 152 152 Q131 149 110 160 Z"}
        fill={brow} opacity={isMan ? 0.95 : 0.85} />
      <path d={isMan ? "M166 150 Q190 140 216 152 L214 160 Q190 149 168 158 Z" : "M168 152 Q190 143 212 154 Q189 149 170 160 Z"}
        fill={brow} opacity={isMan ? 0.95 : 0.85} />

      {/* EYES */}
      {[126, 194].map((cx, i) => (
        <g key={i}>
          {/* eye socket shadow */}
          <path d={`M${cx - 20} 178 Q${cx} 164 ${cx + 20} 178 Q${cx} 194 ${cx - 20} 178 Z`} fill="#fff" />
          <path d={`M${cx - 20} 178 Q${cx} 164 ${cx + 20} 178`} stroke={sDeep} strokeWidth="1" fill="none" opacity="0.4" />
          <clipPath id={`eye${i}${uid}`}><path d={`M${cx - 20} 178 Q${cx} 164 ${cx + 20} 178 Q${cx} 194 ${cx - 20} 178 Z`} /></clipPath>
          <g clipPath={`url(#eye${i}${uid})`}>
            <circle cx={cx} cy="179" r="10.5" fill={g("iris")} />
            <circle cx={cx} cy="179" r="4.6" fill="#17110c" />
            <circle cx={cx + 3} cy="175" r="2" fill="#fff" opacity="0.9" />
          </g>
          {/* upper lash line */}
          <path d={`M${cx - 20} 176 Q${cx} 163 ${cx + 20} 176`} stroke="#24170f" strokeWidth={isMan ? "2" : "3.2"} fill="none" strokeLinecap="round" />
          {!isMan && (<>
            <path d={`M${cx + 17} 173 l7 -5 M${cx + 13} 170 l6 -6 M${cx + 9} 169 l4 -6`} stroke="#24170f" strokeWidth="1.7" strokeLinecap="round" />
          </>)}
          {/* lower lash faint */}
          <path d={`M${cx - 16} 184 Q${cx} 190 ${cx + 16} 184`} stroke={sDeep} strokeWidth="1.4" fill="none" opacity="0.4" />
        </g>
      ))}

      {/* NOSE */}
      <path d="M160 176 C158 190 156 202 150 210 Q160 217 170 210 C164 202 162 190 160 176 Z" fill={sDark} opacity="0.18" />
      <path d="M151 210 q9 6 18 0" stroke={sDeep} strokeWidth="2.4" fill="none" strokeLinecap="round" opacity="0.5" />
      <ellipse cx="153" cy="210" rx="2" ry="1.4" fill={sDeep} opacity="0.35" />
      <ellipse cx="167" cy="210" rx="2" ry="1.4" fill={sDeep} opacity="0.35" />

      {/* LIPS */}
      {isMan ? (
        <path d="M141 230 Q160 224 179 230 Q160 240 141 230 Z" fill={g("lip")} opacity="0.9" />
      ) : (
        <g>
          <path d="M135 229 Q147 220 154 226 Q160 222 166 226 Q173 220 185 229 Q173 244 160 246 Q147 244 135 229 Z" fill={g("lip")} />
          <path d="M135 229 Q160 234 185 229" stroke={lipLo} strokeWidth="1.3" fill="none" />
          <path d="M148 226 Q160 230 172 226" stroke={lipHi} strokeWidth="1.4" fill="none" opacity="0.75" />
          <ellipse cx="160" cy="240" rx="9" ry="2.4" fill={lipHi} opacity="0.5" />
        </g>
      )}

      {/* stubble (man) */}
      {isMan && (
        <path d="M110 210 C126 250 194 250 210 210 C206 254 184 266 160 268 C136 266 114 254 110 210 Z" fill={hDark} opacity="0.16" />
      )}

      {/* FRONT HAIR */}
      {isMan ? (
        <path d="M92 158 C88 100 118 66 160 66 C202 66 232 100 228 158 C228 138 222 116 210 110 C204 124 196 128 190 120 C176 130 144 130 130 120 C124 128 116 124 110 110 C98 116 92 138 92 158 Z" fill={g("hair")} />
      ) : (
        <>
          <path d="M98 156 C92 92 122 62 160 62 C198 62 228 92 224 156 C224 128 214 108 198 104 C194 118 184 122 176 116 C168 126 152 126 144 116 C136 122 126 118 122 104 C106 108 98 128 98 156 Z" fill={g("hair")} />
          {length === "long" && (<>
            <path d="M98 152 C86 224 88 306 104 356 C94 280 100 214 108 168 Z" fill={g("hair")} />
            <path d="M222 152 C234 224 232 306 216 356 C226 280 220 214 212 168 Z" fill={g("hair")} />
            <path d="M108 100 C130 84 190 84 214 104" stroke={hLight} strokeWidth="5" fill="none" strokeLinecap="round" opacity="0.5" />
            <path d="M100 200 C102 250 100 300 108 340" stroke={hLight} strokeWidth="3" fill="none" opacity="0.4" />
          </>)}
          {length === "medium" && (<>
            <path d="M100 154 C94 210 98 252 110 280 C102 240 106 198 110 168 Z" fill={g("hair")} />
            <path d="M220 154 C226 210 222 252 210 280 C218 240 214 198 210 168 Z" fill={g("hair")} />
          </>)}
        </>
      )}
      {/* BLINK eyelids */}
      {blink && [126, 194].map((cx, i) => (
        <g key={`blink${i}`}>
          <path d={`M${cx - 21} 177 Q${cx} 168 ${cx + 21} 177 Q${cx} 185 ${cx - 21} 177 Z`} fill={sMid} />
          <path d={`M${cx - 20} 178 Q${cx} 185 ${cx + 20} 178`} stroke="#24170f" strokeWidth="2.6" fill="none" strokeLinecap="round" />
        </g>
      ))}
    </svg>
    </div>
  );
}
